import { Layout } from "@/components/layout/Layout";
import { formatDuration } from '@/lib/format-duration';
import { TenantImage } from "@/components/home/TenantImage";
import { LoadingLabel, Skel, SkelLines } from "@/components/home/TenantSkeletons";
import { PageExtraSections } from "@/components/sections/PageExtraSections";
import { usePageExtraSections } from "@/hooks/usePageExtraSections";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Sparkles, Building2, Check, ArrowRight, Clock, UserCheck, Calendar, Bed, type LucideIcon } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { useOrg } from "@/contexts/OrgContext";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useServices } from "@/hooks/useServices";
import { useHomepageFeaturedCategories, useHomepageServicePricing } from "@/hooks/useHomepageServices";
import resultsBathroom from "@/assets/results-bathroom.jpg";
import { useServiceAreas } from "@/hooks/useCompanyInfo";
import { useTenantPath } from "@/hooks/useTenantPath";
import { useDemoShowcase } from "@/hooks/useDemoShowcase";
import { formatAreas } from "@/lib/service-areas";
import { CTAPanel } from "@/components/home/CTAPanel";
import { CallbackDialog } from "@/components/CallbackDialog";
import { formatMoney } from "@/lib/currency";
import { PageSeo } from "@/components/seo/PageSeo";
import cleanLivingRoom from "@/assets/clean-living-room.jpg";
import cleanKitchen from "@/assets/clean-kitchen.jpg";
import cleanOffice from "@/assets/clean-office.jpg";

// Main service categories
const serviceCategories = [
  {
    id: "regular",
    icon: Home,
    title: "Regular Cleaning",
    subtitle: "Weekly, fortnightly or monthly home cleaning",
    description: "Keep your home consistently spotless with our regular cleaning service. We'll assign the same trusted cleaner to your home, so they get to know your preferences and deliver a personalised service every time.",
    price: "25/hr",
    image: cleanLivingRoom,
    pricingTiers: [
      { bedrooms: "1-2 bedroom home", hours: "2 hour minimum" },
      { bedrooms: "3-4 bedroom home", hours: "3 hour minimum" },
      { bedrooms: "5+ bedroom home", hours: "4 hour minimum" },
    ],
    included: [
      "Surface cleaning – tables, shelves, countertops dusted and wiped",
      "Vacuuming and mopping all floors",
      "Bathroom cleaning and sanitisation",
      "Kitchen cleaning – worktops, sink, hob and appliance exteriors",
      "Changing bed linen",
      "Emptying bins",
      "Windows and mirrors (accessible)",
      "Tidying and organising",
    ],
    extras: [
      "Ironing service available",
      "Inside fridge/oven on request",
      "We can bring our own products",
    ],
    cta: "Book Regular Clean",
    ctaLink: "/booking",
  },
  {
    id: "one-off",
    icon: Sparkles,
    title: "One Off Cleaning",
    subtitle: "Deep Clean • End of Tenancy • Airbnb Turnovers",
    description: "Need a thorough clean for a specific occasion? Whether you're moving out, preparing for guests, or your property needs a deep refresh between Airbnb bookings, our one off cleaning covers it all.",
    price: "From 80",
    image: cleanKitchen,
    serviceTypes: [
      {
        name: "Airbnb and Holiday Let Turnovers",
        description: "Quick, reliable turnovers between guests. Photo ready presentation guaranteed.",
        features: ["Complete property clean", "Linen change and bed making", "Towel replacement", "Restock essentials checklist", "Rubbish removal", "Same day availability"],
      },
      {
        name: "End of Tenancy Clean",
        description: "Get your deposit back with our comprehensive moving out clean designed to meet letting agent standards.",
        features: ["Full deep clean of entire property", "Inside all cupboards and drawers", "Limescale removal from bathrooms", "Oven and hob professional clean", "Carpet cleaning available", "Inventory checklist compliance"],
      },
      {
        name: "Deep Clean",
        description: "A thorough, intensive clean for when your home needs extra attention \u2013 spring cleaning, pre event or post renovation.",
        features: ["Everything in regular clean, plus:", "Inside oven, hob and extractor", "Inside fridge and freezer", "Skirting boards and door frames", "Light fixtures and switches", "Behind and under all furniture", "Window cleaning (interior)"],
      },
    ],
    cta: "Get a Quote",
    ctaLink: "/booking",
  },
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial Cleaning",
    subtitle: "Offices, retail spaces and business premises",
    description: "Professional cleaning for businesses of all sizes. Whether you need a one off clean or a scheduled regular service, we work around your business hours to minimise disruption and keep your workspace pristine.",
    price: "Request a Quote",
    image: cleanOffice,
    included: [
      "Office and desk cleaning",
      "Reception and common areas",
      "Kitchen and break room facilities",
      "Bathroom and toilet sanitisation",
      "Floor care and maintenance",
      "Window cleaning",
      "Waste removal and recycling",
      "Flexible scheduling around your hours",
    ],
    options: [
      { name: "One off clean", description: "Perfect for post-event or seasonal deep cleans" },
      { name: "Regular scheduled service", description: "Daily, weekly or monthly visits tailored to your needs" },
    ],
    cta: "Request a Quote",
    ctaLink: "/booking",
  },
];

const CATEGORY_IMAGES = [cleanLivingRoom, cleanKitchen, cleanOffice, resultsBathroom];

const ServicesPage = () => {
  const { orgId, loading: orgLoading } = useOrg();
  const tenantPath = useTenantPath();
  const demo = useDemoShowcase();
  const { data: orgSettings } = useOrgSettings(orgLoading ? undefined : orgId);
  const { data: services, isLoading: servicesLoading } = useServices(orgLoading ? undefined : orgId);
  const { data: serviceAreas = [], isPending: areasPending } = useServiceAreas(orgLoading ? undefined : orgId);
  // Drive the page from the tenant's real services (Roadmap 1H). The hardcoded
  // marketing copy below is only a fallback for tenants that haven't added any.
  const hasRealServices = !servicesLoading && (services?.length ?? 0) > 0;
  const regularPrice = `${formatMoney(25, orgSettings?.currency)}/hr`;
  const oneOffPrice = formatMoney(80, orgSettings?.currency, { from: true });
  const { data: featuredCategories = [] } = useHomepageFeaturedCategories(orgLoading ? undefined : orgId);
  // Same category imagery as the homepage cards (tenant-uploaded first, then stock).
  // Same query (and therefore same category order) the homepage cards use.
  const { data: pricingRows = [] } = useHomepageServicePricing(orgLoading ? undefined : orgId);
  const serviceCategoryNames = Array.from(
    new Set([...pricingRows.map((row) => row.category), ...(services ?? []).map((service) => service.category)].filter(Boolean)),
  ) as string[];
  // Mirror the homepage card order: featured categories first, then the rest.
  const categoryOrder = [
    ...featuredCategories.map((item) => item.category).filter((category) => serviceCategoryNames.includes(category)),
    ...serviceCategoryNames.filter((category) => !featuredCategories.some((item) => item.category === category)),
  ];
  const imageForCategory = (category: string | null) => {
    if (!category) return CATEGORY_IMAGES[0];
    const featured = featuredCategories.find((item) => item.category === category);
    if (featured?.imageUrl) return featured.imageUrl;
    const index = Math.max(categoryOrder.indexOf(category), 0);
    return CATEGORY_IMAGES[index % CATEGORY_IMAGES.length];
  };
  const serviceCount = services?.length ?? 0;
  const { data: extraSections } = usePageExtraSections(orgLoading ? undefined : orgId, "services");
  const areaText = serviceAreas.length > 0 ? formatAreas(serviceAreas) : "your area";

  return (
    <Layout>
      <PageSeo pageSlug="services" fallbackTitle="Cleaning Services" fallbackDescription="View cleaning services, pricing, and booking options." />
      {/* Hero */}
      <section className="relative -mt-20 overflow-hidden pb-14 pt-[120px] md:pb-20 md:pt-36">
        <div className="tp-hero-wash" aria-hidden />
        <div className="container relative mx-auto px-5 md:px-6">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <p className="tp-eyebrow" data-reveal>Services &amp; pricing</p>
              <h1 className="tp-display mt-6" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>
                {renderDisplayText(demo?.servicesPage.title ?? "Our Cleaning Services")}
              </h1>
            </div>
            <div className="lg:col-span-5 lg:pb-2" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
              {demo ? (
                <p className="tp-lead tp-loaded">{demo.servicesPage.lead}</p>
              ) : areasPending ? (
                <div className="tp-lead tp-skeleton" aria-hidden>
                  <SkelLines lines={4} widths={["100%", "96%", "98%", "40%"]} />
                </div>
              ) : (
                <p className="tp-lead tp-loaded">
                  Professional cleaning services across {areaText}. From regular home cleaning to specialist deep cleans and commercial contracts, we have a service for every need.
                </p>
              )}
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-2.5" data-reveal style={{ ["--reveal-delay" as string]: "200ms" }}>
            <span className="tp-chip"><UserCheck className="h-4 w-4 text-primary" />Vetted staff</span>
            <span className="tp-chip"><Check className="h-4 w-4 text-primary" />Fully insured</span>
            <span className="tp-chip"><Clock className="h-4 w-4 text-primary" />Flexible scheduling</span>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="pb-20 md:pb-28">
        <div className="container mx-auto px-5 md:px-6">
          {orgLoading || servicesLoading ? (
            <div className="tp-skeleton grid gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3" aria-busy="true">
              <LoadingLabel>Loading services</LoadingLabel>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div key={index} className={cn(index >= 2 && "hidden md:block", index >= 4 && "md:hidden lg:block")}>
                  <ServiceCardSkeleton />
                </div>
              ))}
            </div>
          ) : hasRealServices ? (
            <div className={cn("grid gap-5 md:grid-cols-2 md:gap-6", serviceCount % 3 === 0 || serviceCount > 4 ? "lg:grid-cols-3" : "")}>
              {services!.map((service, index) => (
                <article
                  key={service.id}
                  id={service.id}
                  className="tp-card tp-card--interactive group flex flex-col p-2.5"
                  data-reveal
                  style={{ ["--reveal-delay" as string]: `${(index % 3) * 90}ms` }}
                >
                  <div className="tp-media tp-media--inner relative aspect-[16/9]">
                    <TenantImage src={imageForCategory(service.category)} alt="" className="tp-zoom" loading="lazy" />
                    {service.category && (
                      <span className="tp-glass absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-medium text-foreground">{service.category}</span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col px-4 pb-4 pt-6 md:px-5">
                    <div className="flex items-start justify-between gap-4">
                      <h2 className="tp-h3">
                        {renderDisplayText(service.name)}
                      </h2>
                      <span className="shrink-0 pt-1 text-base font-semibold text-primary">
                        {service.is_quote_only
                          ? "Request a quote"
                          : formatMoney(service.price, orgSettings?.currency, { from: true })}
                      </span>
                    </div>
                    {service.description && (
                      <p className="mt-3 leading-relaxed text-muted-foreground">
                        {renderDisplayText(service.description)}
                      </p>
                    )}
                    <div className="min-h-6 flex-1" aria-hidden />
                    <div className="flex items-center justify-between gap-4 border-t tp-hairline pt-5">
                      <span className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {formatDuration(service.duration)}
                      </span>
                      <Link to={tenantPath("/booking")} className="tp-link text-sm">
                        {service.is_quote_only ? "Get a Quote" : "Book Now"}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
          <div className="space-y-24 md:space-y-32">
            {/* Regular Cleaning */}
            <FallbackCategory
              id="regular"
              icon={Home}
              title={serviceCategories[0].title}
              subtitle={serviceCategories[0].subtitle}
              price={regularPrice}
              description={serviceCategories[0].description}
              image={serviceCategories[0].image}
              cta={serviceCategories[0].cta}
              ctaHref={tenantPath(serviceCategories[0].ctaLink)}
            >
              <div className="tp-card p-6 md:p-7">
                <h3 className="tp-h4 flex items-center gap-2">
                  <Bed className="h-5 w-5 text-primary" />
                  Minimum hours by property size
                </h3>
                <div className="mt-4 divide-y divide-foreground/[0.07]">
                  {serviceCategories[0].pricingTiers?.map((tier) => (
                    <div key={tier.bedrooms} className="flex items-center justify-between py-3">
                      <span className="text-foreground">{tier.bedrooms}</span>
                      <span className="font-medium text-primary">{tier.hours}</span>
                    </div>
                  ))}
                </div>
              </div>

              <IncludedList title="What's included:" items={serviceCategories[0].included ?? []} />

              <div className="flex flex-wrap gap-2">
                {serviceCategories[0].extras?.map((extra) => (
                  <span key={extra} className="tp-chip">+ {extra}</span>
                ))}
              </div>
            </FallbackCategory>

            {/* One-Off Cleaning */}
            <FallbackCategory
              id="one-off"
              reverse
              icon={Sparkles}
              title={serviceCategories[1].title}
              subtitle={serviceCategories[1].subtitle}
              price={oneOffPrice}
              description={serviceCategories[1].description}
              image={serviceCategories[1].image}
              cta={serviceCategories[1].cta}
              ctaHref={tenantPath(serviceCategories[1].ctaLink)}
            >
              <div className="space-y-4">
                {serviceCategories[1].serviceTypes?.map((service) => (
                  <div key={service.name} className="tp-card p-6 md:p-7">
                    <h3 className="tp-h4 text-lg">{renderDisplayText(service.name)}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground">{service.description}</p>
                    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2">
                          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                          <span className="text-sm text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </FallbackCategory>

            {/* Commercial Cleaning */}
            <FallbackCategory
              id="commercial"
              icon={Building2}
              title={serviceCategories[2].title}
              subtitle={serviceCategories[2].subtitle}
              price={serviceCategories[2].price}
              description={serviceCategories[2].description}
              image={serviceCategories[2].image}
              cta={serviceCategories[2].cta}
              ctaHref={tenantPath(serviceCategories[2].ctaLink)}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                {serviceCategories[2].options?.map((option) => (
                  <div key={option.name} className="tp-card p-5">
                    <h4 className="tp-h4 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      {option.name}
                    </h4>
                    <p className="mt-1.5 text-sm text-muted-foreground">{option.description}</p>
                  </div>
                ))}
              </div>

              <IncludedList title="What's included:" items={serviceCategories[2].included ?? []} />
            </FallbackCategory>
          </div>
          )}
        </div>
      </section>

      <PageExtraSections sections={extraSections} />

      <CTAPanel
        eyebrow={demo?.servicesPage.ctaEyebrow ?? "Need a hand choosing?"}
        title={demo?.servicesPage.ctaTitle ?? "Not sure which service you need?"}
        subtitle={demo?.servicesPage.ctaSubtitle ?? "Get in touch and we'll help you find the perfect cleaning solution for your home or business."}
        buttonText="Get a free quote"
        buttonHref={tenantPath("/booking")}
        secondary={<CallbackDialog variant="cta" />}
        image={cleanKitchen}
      />
      <div className="h-10 md:h-16" aria-hidden />
    </Layout>
  );
};

/** Same footprint as a real service card: 16:9 photo, title + price, copy, footer row. */
function ServiceCardSkeleton() {
  return (
    <div className="tp-card flex flex-col p-2.5" aria-hidden>
      <Skel className="aspect-[16/9] rounded-[calc(var(--tp-radius-card)-0.4rem)]" />
      <div className="flex flex-1 flex-col px-4 pb-4 pt-6 md:px-5">
        <div className="flex items-start justify-between gap-4">
          <Skel className="h-6 w-1/2 tp-skel-round" />
          <Skel className="h-5 w-16 tp-skel-round" />
        </div>
        <div className="mt-4 space-y-2.5">
          <Skel className="h-3.5 w-full tp-skel-round" />
          <Skel className="h-3.5 w-4/5 tp-skel-round" />
        </div>
        <div className="mt-6 flex items-center justify-between gap-4 border-t tp-hairline pt-5">
          <Skel className="h-3.5 w-20 tp-skel-round" />
          <Skel className="h-3.5 w-24 tp-skel-round" />
        </div>
      </div>
    </div>
  );
}

function IncludedList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="tp-card p-6 md:p-7">
      <h3 className="tp-h4">{renderDisplayText(title)}</h3>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {items.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span className="text-sm text-foreground">{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface FallbackCategoryProps {
  id: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  price: string;
  description: string;
  image: string;
  cta: string;
  ctaHref: string;
  reverse?: boolean;
  children: React.ReactNode;
}

/** Editorial split layout used for the default (no services yet) categories. */
function FallbackCategory({ id, icon: Icon, title, subtitle, price, description, image, cta, ctaHref, reverse, children }: FallbackCategoryProps) {
  return (
    <div id={id} className="scroll-mt-28">
      <div className={cn("grid items-start gap-10 lg:grid-cols-12 lg:gap-14", reverse && "lg:[&>*:first-child]:order-2")}>
        <div className="lg:sticky lg:top-28 lg:col-span-5" data-reveal="mask">
          <div className="tp-media tp-shadow-md relative aspect-[4/3] lg:aspect-[4/5]">
            <TenantImage src={image} alt={title} loading="lazy" />
          </div>
        </div>

        <div className="space-y-6 lg:col-span-7">
          <div data-reveal>
            <span className="tp-icon-chip"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
            <h2 className="tp-h2 mt-6">{renderDisplayText(title)}</h2>
            <p className="mt-3 text-muted-foreground">{subtitle}</p>
            <span className="mt-3 block text-xl font-semibold text-primary">{price}</span>
            <p className="tp-lead mt-5">{description}</p>
          </div>

          <div className="space-y-6" data-reveal style={{ ["--reveal-delay" as string]: "100ms" }}>
            {children}
          </div>

          <Button variant="hero" size="lg" className="tp-btn group hover:translate-y-0" asChild>
            <Link to={ctaHref}>
              {cta}
              <span className="tp-btn-arrow"><ArrowRight /></span>
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ServicesPage;
